import React, { useState, useEffect, useRef } from "react";
import { WEDDING } from "../utils/config";

// Put your MP3 inside: src/assets/music/
// (Update the path below if your folder name is different)
import weddingSong from "../assets/Music/Gods Timing Gave Me You.mp3";

export default function MusicPlayer() {
  const [visible, setVisible] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [progress, setProgress] = useState(0);

  const audioRef = useRef(null);

  // Show player and toast
  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 2000);
    const t2 = setTimeout(() => setShowToast(true), 2800);
    const t3 = setTimeout(() => setShowToast(false), 7500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  // Keep UI synced with audio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Restore previous playback position
    const savedTime = sessionStorage.getItem("wedding-song-position");
    if (savedTime) {
      audio.currentTime = Number(savedTime);
    }

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    const onTimeUpdate = () => {
      if (!audio.duration) return;

      setProgress((audio.currentTime / audio.duration) * 100);

      sessionStorage.setItem(
        "wedding-song-position",
        audio.currentTime.toString(),
      );
    };

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("timeupdate", onTimeUpdate);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, []);

  // Play / Pause
  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      try {
        await audio.play();
      } catch (err) {
        console.error("Playback failed:", err);
      }
    } else {
      audio.pause();
    }
  };

  const trackName = WEDDING.tracks?.[0]?.split(" — ") ?? ["Our Song", ""];

  return (
    <>
      {/* Hidden Audio */}
      <audio ref={audioRef} loop preload="metadata">
        <source src={weddingSong} type="audio/mpeg" />
      </audio>

      {/* Toast */}
      <div
        style={{
          position: "fixed",
          bottom: "78px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1001,
          background: "var(--forest)",
          color: "var(--mint-l)",
          padding: "10px 26px",
          borderRadius: "30px",
          fontSize: "12px",
          letterSpacing: ".1em",
          whiteSpace: "nowrap",
          border: ".5px solid rgba(126,207,184,.3)",
          opacity: showToast ? 1 : 0,
          transition: "opacity .5s",
          pointerEvents: "none",
        }}
      >
        🎵 Tap play to hear our song
      </div>

      {/* Music Player */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: "rgba(21,32,24,.97)",
          backdropFilter: "blur(24px)",
          borderTop: ".5px solid rgba(126,207,184,.18)",
          padding: "12px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "10px",
          transform: visible ? "translateY(0)" : "translateY(100%)",
          transition: "transform .55s cubic-bezier(.22,1,.36,1)",
        }}
      >
        {/* Song Info */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            className={playing ? "disc-spin" : ""}
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "conic-gradient(#6B9B6F,#7ECFB8,#3E6642,#6B9B6F)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "13px",
              flexShrink: 0,
            }}
          >
            🎵
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: "13px",
                color: "#fff",
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {trackName[0]}
            </div>

            <div
              style={{
                fontSize: "10px",
                color: "var(--mint-l)",
                letterSpacing: ".08em",
                marginTop: "2px",
              }}
            >
              {trackName[1]} · {playing ? "Now Playing" : "Paused"}
            </div>

            {/* Progress Bar */}
            <div
              style={{
                marginTop: "8px",
                width: "100%",
                height: "3px",
                background: "rgba(255,255,255,.12)",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  background: "var(--mint)",
                  transition: "width .2s linear",
                }}
              />
            </div>
          </div>
        </div>

        {/* Play Button */}
        <button
          onClick={toggle}
          style={{
            padding: "9px 22px",
            borderRadius: "22px",
            border: ".5px solid rgba(126,207,184,.4)",
            background: playing ? "rgba(126,207,184,.15)" : "transparent",
            color: "var(--mint-l)",
            fontSize: "11px",
            fontWeight: 500,
            letterSpacing: ".12em",
            textTransform: "uppercase",
            cursor: "pointer",
            transition: ".25s",
            flexShrink: 0,
          }}
        >
          {playing ? "⏸ Pause" : "▶ Play"}
        </button>
      </div>
    </>
  );
}
